import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTransformOptions } from 'Core/hooks';
import { ProductionService } from '../../service/api/production-service';

const initData = {
  userId: '',
  carrierName: '',
  carrierNumber: '',
  carrierIdExt: '',
  location: '',
  status: '',
  label: '',
  carrierShortName: '',
  email: '',
  phone: '',
  carrierContact: '',
  labelPrinter: 0,
  documentPrinter: 0,
  isDynamicShippingDocument: false,
  domain: '',
};

const CarriersAddEditForm = ({ data, onChanges }) => {
  const statusesList = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ];

  const statusesOptions = useTransformOptions(statusesList, 'value', 'label', '', 'Select Status');
  const [printerOptions, setPrinterOptions] = useState([]);

  const [formData, setFormData] = useState(data ? data : initData);

  useEffect(() => {
    if (data) setFormData(data);
  }, [data]);

  const updateFormField = (fieldName, value) => {
    setFormData(prevData => {
      const next = { ...prevData, [fieldName]: value };
      onChanges(next);
      return next;
    });
  };

  const printerDropdownOptions = printerOptions.map(p => ({
    label: p.printerName,
    value: p.printerId,
  }));

  useEffect(() => {
    const fetchPrinterOptions = async () => {
      try {
        const response = await ProductionService.getPrinters();
        if (response.status === 200) {
          setPrinterOptions(response.response || []);
        }
      } catch (error) {
        console.error('Failed to fetch printer options:', error);
      }
    };

    fetchPrinterOptions();
  }, []);

  return (
    <>
      <div className="row">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Carrier Name</label>
            <input
              name="carrierName"
              type="text"
              className="form-control"
              value={formData.carrierName || ''}
              onChange={e => updateFormField('carrierName', e.target.value)}
            />
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Carrier Short Name</label>
            <input
              name="carrierShortName"
              type="text"
              className="form-control"
              maxLength={10}
              value={formData.carrierShortName || ''}
              onChange={e => updateFormField('carrierShortName', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Location</label>
            <input
              type="text"
              value={formData.location || ''}
              onChange={e => updateFormField('location', e.target.value)}
              className="form-control"
            />
          </div>
        </div>

        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Status</label>
            <select
              value={formData.status || ''}
              onChange={e => updateFormField('status', e.target.value)}
              className="form-control"
            >
              {statusesOptions.map(option => (
                <option key={option.label} value={option.label}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Label</label>
            <input
              type="text"
              value={formData.label || ''}
              onChange={e => updateFormField('label', e.target.value)}
              className="form-control"
            />
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Carrier Number</label>
            <input
              type="text"
              value={formData.carrierNumber || ''}
              onChange={e => updateFormField('carrierNumber', e.target.value)}
              className="form-control"
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Carrier External ID</label>
            <input
              type="text"
              value={formData.carrierIdExt || ''}
              onChange={e => updateFormField('carrierIdExt', e.target.value)}
              className="form-control"
            />
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Domain</label>
            <input
              name="domain"
              type="text"
              className="form-control"
              value={formData.domain || ''}
              onChange={e => updateFormField('domain', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-4 col-md-4 col-sm-12">
          <div className="input-blocks">
            <label>Email</label>
            <input
              type="text"
              value={formData.email || ''}
              onChange={e => updateFormField('email', e.target.value)}
              className="form-control"
            />
          </div>
        </div>
        <div className="col-lg-4 col-md-4 col-sm-12">
          <div className="input-blocks">
            <label>Phone</label>
            <input
              type="text"
              value={formData.phone || ''}
              onChange={e => updateFormField('phone', e.target.value)}
              className="form-control"
            />
          </div>
        </div>
        <div className="col-lg-4 col-md-4 col-sm-12">
          <div className="input-blocks">
            <label>Carrier Contact</label>
            <input
              type="text"
              value={formData.carrierContact || ''}
              onChange={e => updateFormField('carrierContact', e.target.value)}
              className="form-control"
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Label Printer</label>
            <select
              value={formData.labelPrinter ?? 0}
              onChange={e => updateFormField('labelPrinter', Number(e.target.value))}
              className="form-control"
            >
              <option value="">Select Label Printer</option>
              {printerDropdownOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Document Printer</label>
            <select
              value={formData.documentPrinter ?? 0}
              onChange={e => updateFormField('documentPrinter', Number(e.target.value))}
              className="form-control"
            >
              <option value="">Select Document Printer</option>
              {printerDropdownOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label className="form-label d-block mb-2">Options</label>
            <div className="form-check" style={{ marginTop: '8px' }}>
              <input
                type="checkbox"
                className="form-check-input"
                id="isDynamicShippingDocument"
                checked={formData.isDynamicShippingDocument || false}
                onChange={e => updateFormField('isDynamicShippingDocument', e.target.checked)}
                style={{
                  transform: 'scale(1.8)',
                  marginRight: '12px',
                  width: '20px',
                  height: '20px',
                  accentColor: '#007bff',
                }}
              />
              <label
                className="form-check-label"
                htmlFor="isDynamicShippingDocument"
                style={{ fontSize: '15px', fontWeight: '500', lineHeight: '1.5' }}
              >
                Is Dynamic Shipping Document
              </label>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

CarriersAddEditForm.propTypes = {
  data: PropTypes.object,
  onChanges: PropTypes.func,
};

export default CarriersAddEditForm;

